/**
 * alert-settings-service.js
 *
 * Reads and writes per-user Telegram + email alert preferences to Firestore.
 *
 * Firestore layout:
 *   telegramSubscribers/{uid}   — queried by the server alert-service
 *   telegramLinkTokens/{token}  — short-lived tokens for bot connection
 *   users/{uid}.emailAlertsEnabled — email opt-in flag, read from the same
 *     doc the user's tier and email already live on (no separate "connect"
 *     step needed — unlike Telegram, the email address is already known).
 */

import { db } from './firebase-config';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';

// All strategy keys in display order
export const ALL_STRATEGIES = [
  { key: 'triple-alignment', label: 'Triple Alignment (W+D+4H)', tier: 'basic'  },
  { key: 'double-alignment', label: 'Double Alignment (2 of 3)', tier: 'basic'  },
  { key: 'rubberband',       label: 'RBPullback',                 tier: 'pro'    },
  { key: '20-50-pullback',   label: '20-50 Pullback',             tier: 'pro'    },
  { key: 'rsi-mtf',          label: 'RSI Multi-Timeframe',        tier: 'elite'  },
  { key: 'cci-pullback',     label: 'CCI Pullback',               tier: 'elite'  },
  { key: 'mtf-50sma',        label: 'MTF 50 SMA',                 tier: 'elite'  },
  { key: 'topdog',           label: 'ProjectTD',                  tier: 'elite'  },
  { key: 'topdog-cci-combined', label: 'ProjectTD + CCI Combined', tier: 'elite'  },
  { key: 'trend-pullback',   label: 'Trend Pullback',             tier: 'elite'  },
  { key: '20ema-sr',         label: '20 EMA S/R',                 tier: 'elite'  },
  { key: '20-8ema-parabolic', label: '20/8 EMA Parabolic',        tier: 'elite'  },
  { key: 'ma-crossover',     label: 'MA Crossover',               tier: 'elite'  },
  { key: 'kc-extreme',       label: 'KC Extreme',                 tier: 'elite'  },
  { key: 'volume-spike',     label: 'Volume Spike',               tier: 'basic'  },
];

function subscriberRef(uid) {
  return doc(db, 'telegramSubscribers', uid);
}

function linkTokenRef(token) {
  return doc(db, 'telegramLinkTokens', token);
}

function userRef(uid) {
  return doc(db, 'users', uid);
}

/** Whether the user has opted into email alerts. Defaults to false. */
export async function getEmailAlertsEnabled(uid) {
  const snap = await getDoc(userRef(uid));
  return snap.exists() ? !!snap.data().emailAlertsEnabled : false;
}

/** Enable or disable email alerts — writes directly to users/{uid}, read by the server. */
export async function setEmailAlertsEnabled(uid, enabled) {
  await setDoc(userRef(uid), { emailAlertsEnabled: enabled }, { merge: true });
}

/** Load current alert settings for a user. Returns null if not yet configured. */
export async function getAlertSettings(uid) {
  const snap = await getDoc(subscriberRef(uid));
  return snap.exists() ? snap.data() : null;
}

/** Subscribe to real-time changes (used after showing the connect link). */
export function onAlertSettingsChange(uid, callback, onError) {
  return onSnapshot(
    subscriberRef(uid),
    snap => callback(snap.exists() ? snap.data() : null),
    err => { if (onError) onError(err); }
  );
}

/** Save alert preferences. Writes to telegramSubscribers so the server can query it. */
export async function saveAlertSettings(uid, { telegramEnabled, strategies }) {
  const existing = await getAlertSettings(uid);
  await setDoc(subscriberRef(uid), {
    uid,
    telegramEnabled,
    strategies,
    chatId:    existing?.chatId || null,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

/** Generate a one-time link token so the bot can auto-capture the user's chat ID. */
export async function generateLinkToken(uid) {
  // 8-char random alphanumeric token
  const token = Math.random().toString(36).slice(2, 6) + Math.random().toString(36).slice(2, 6);
  await setDoc(linkTokenRef(token), {
    uid,
    createdAt:  new Date().toISOString(),
    expiresAt:  new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  });
  return token;
}

/** Disconnect Telegram — clears chatId but keeps strategy preferences. */
export async function disconnectTelegram(uid) {
  await setDoc(subscriberRef(uid), {
    chatId:          null,
    telegramEnabled: false,
  }, { merge: true });
}
